import React from 'react';
import AddSessionPanel from './AddSessionPanel';
import DeleteScheduleControl from './DeleteScheduleControl';
import RescheduleControl from './RescheduleControl';
import HealPanel from './HealPanel';
import LinkContentPanel from './LinkContentPanel';
import SessionHealButton from './SessionHealButton';

const FLAG_TEXT = {
  empty: 'No content linked to this schedule',
  missing_exam: 'Exam session — no exam content linked',
  missing_live: 'Live session — no live (video conference) content linked',
  revision_live_unlinked: 'Revision Live — no exam-discussion conference linked yet',
};

const TYPE_ICON = {
  Exam: 'bi-clipboard-check',
  VideoConference: 'bi-camera-video',
  Video: 'bi-play-circle',
  Attachment: 'bi-paperclip',
};

function fmtDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function hasDates(c) {
  return c.content_type === 'Exam' || c.content_type === 'VideoConference';
}

const DatePill = ({ icon, label, value, tone }) => {
  const text = fmtDate(value);
  if (!text) return null;
  return (
    <span className={`cal-datepill cal-datepill-${tone}`}>
      <i className={`bi ${icon}`} aria-hidden="true" />
      <span className="cal-datepill-label">{label}</span>
      <span className="cal-datepill-value">{text}</span>
    </span>
  );
};

const DayDetail = ({
  date,
  items,
  onClose,
  isoDate,
  month,
  courses = [],
  batchIds = [],
  groupLabel,
  onScheduleMoved,
  onHealed,
}) => {
  const flaggedCount = items.filter((it) => it.flags && it.flags.length).length;

  return (
    <div className="mentors-modal-overlay" onClick={onClose}>
      <div className="mentors-modal cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mentors-modal-header">
          <h2 className="mentors-modal-title">
            <i className="bi bi-calendar-day" aria-hidden="true" />&nbsp;{date}
            <span className="cal-modal-count">
              {items.length} session{items.length === 1 ? '' : 's'}
              {flaggedCount > 0 && <span className="cal-modal-count-flag"> · {flaggedCount} flagged</span>}
            </span>
          </h2>
          <button className="mentors-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="mentors-modal-body">
          {isoDate && (
            <AddSessionPanel
              isoDate={isoDate}
              batchIds={batchIds}
              groupLabel={groupLabel}
              courses={courses}
              onCreated={onHealed}
            />
          )}

          {isoDate && (
            <HealPanel
              isoDate={isoDate}
              month={month}
              onHealed={onHealed}
            />
          )}

          <ol className="cal-timeline">
            {items.map((it) => {
              const flagged = it.flags && it.flags.length > 0;
              const scheduleIds = it.scheduleIds || (it.id ? [it.id] : []);
              return (
                <li key={it.groupKey || it.id} className={`cal-tl-item${flagged ? ' cal-tl-item-flagged' : ''}`}>
                  <span className="cal-tl-time">{it.time || '—'}</span>
                  <span className="cal-tl-node" aria-hidden="true" />

                  <div className="cal-tl-body">
                    <div className="cal-tl-head">
                      <span className="cal-tl-subject">{it.subject || 'Class'}</span>
                      <span className={`cal-tl-mode cal-tl-mode-${(it.mode || '').toLowerCase().replace(/\s+/g, '-')}`}>
                        {it.mode || '—'}
                      </span>
                    </div>

                    <div className="cal-tl-meta">
                      Class {it.classNumber}
                      {it.chapter ? ` · ${it.chapter.name}` : ''}
                      {it.chapter && it.chapter.topic ? ` › ${it.chapter.topic.name}` : ''}
                      {it.section ? ` · Sec ${it.section}` : ''}
                      {it.faculty ? ` · ${it.faculty}` : ''}
                      {it.batchCount > 1 && (
                        <span className="cal-tl-batches"> · All {it.batchCount} batches</span>
                      )}
                      <RescheduleControl
                        scheduleIds={scheduleIds}
                        currentDate={isoDate}
                        onMoved={onScheduleMoved}
                      />
                      <DeleteScheduleControl
                        scheduleIds={scheduleIds}
                        label={`${it.subject || 'this class'} · Class ${it.classNumber}`}
                        onDeleted={onScheduleMoved}
                      />
                    </div>

                    {flagged && (
                      <div className="cal-tl-flag">
                        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                        {FLAG_TEXT[it.flags[0]] || it.flags[0]}
                      </div>
                    )}

                    {flagged && isoDate && (
                      <div className="cal-tl-repair">
                        <SessionHealButton
                          isoDate={isoDate}
                          flag={it.flags[0]}
                          onHealed={onHealed}
                        />
                        <LinkContentPanel
                          scheduleIds={scheduleIds}
                          flag={it.flags[0]}
                          mode={it.mode}
                          courses={courses}
                          onLinked={onHealed}
                        />
                      </div>
                    )}

                    {it.contents.length === 0 ? (
                      <div className="cal-content-empty">No linked content</div>
                    ) : (
                      <ul className="cal-content-list">
                        {it.contents.map((c) => (
                          <li key={c.contentId} className="cal-content-card">
                            <div className="cal-content-top">
                              <span className={`cal-content-badge cal-content-badge-${c.content_type.toLowerCase()}`}>
                                <i className={`bi ${TYPE_ICON[c.content_type] || 'bi-file-earmark'}`} aria-hidden="true" />
                                {c.content_type}
                              </span>
                              <span className="cal-content-title">{c.title}</span>
                              {c.content_type === 'Exam' && c.examId != null && (
                                <span className="cal-content-ref">#{c.examId}</span>
                              )}
                              {c.content_type === 'VideoConference' && c.state && (
                                <span className={`cal-content-state cal-content-state-${c.state.toLowerCase()}`}>{c.state}</span>
                              )}
                            </div>

                            {hasDates(c) && (
                              <div className="cal-content-dates">
                                <DatePill icon="bi-plus-circle" label="Created" value={c.created} tone="created" />
                                <DatePill icon="bi-pencil" label="Modified" value={c.modified} tone="modified" />
                                <DatePill icon="bi-clock" label={c.content_type === 'Exam' ? 'Starts' : 'Scheduled'} value={c.start} tone="start" />
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default DayDetail;
